import { useMemo } from 'react';
import {
  useGetUserLeagues,
  useCreateLeague,
  useJoinLeague,
  useLeaveLeague,
  useDeleteLeague,
} from '@/api/query/useLeagues';
import { toLeagueId, toUserId } from '@/utils/id-helpers';
import { League } from '../types/user';
import { ApiLeagueResponse } from './useLeagueDetails';

function transformLeague(apiLeague: ApiLeagueResponse, index: number): League {
  return {
    id: toLeagueId(apiLeague.league_id ?? apiLeague.id),
    name: apiLeague.title,
    isOwner: apiLeague.is_owner ?? false,
    color: index % 2 === 0 ? 'orange' : 'blue',
    memberCount: apiLeague.current_members ?? 0,
    members: [],
    createdBy: toUserId(apiLeague.creator_id ?? apiLeague.owner_id ?? apiLeague.owner_id ?? ''),
    code: apiLeague.league_code ?? apiLeague.code,
    isPublic: apiLeague.is_public ?? false,
  };
}

export const useLeagues = () => {
  const userLeaguesQuery = useGetUserLeagues();
  const createLeagueMutation = useCreateLeague();
  const joinLeagueMutation = useJoinLeague();
  const leaveLeagueMutation = useLeaveLeague();
  const deleteLeagueMutation = useDeleteLeague();

  // Transform API leagues to UI format
  const leagues: League[] = useMemo(() => {
    if (!userLeaguesQuery.data?.leagues) return [];

    return userLeaguesQuery.data.leagues.map((league, index) =>
      transformLeague(league as ApiLeagueResponse, index)
    );
  }, [userLeaguesQuery.data]);

  // Get specific league details
  const getLeague = (id: string): League | undefined => {
    return leagues.find(league => league.id === id);
  };

  // Note: getLeagueWithMembers should be used as a separate hook
  // This returns a hook factory function

  // Get user's leagues
  const getUserLeagues = (userId: string): League[] => {
    return leagues.filter(league => league.members.some(member => member.id === userId));
  };

  // Get leagues created by user
  const getCreatedLeagues = (userId: string): League[] => {
    return leagues.filter(league => league.createdBy === userId);
  };

  // Check if user is league creator
  const isLeagueCreator = (leagueId: string, userId: string): boolean => {
    const league = getLeague(leagueId);
    return league?.createdBy === userId;
  };

  // Create a new friends league and return the API payload for the share screen.
  const createLeague = async (title: string) => {
    return createLeagueMutation.mutateAsync({ title });
  };

  // Join a league
  const joinLeague = async (code: string) => {
    return joinLeagueMutation.mutateAsync({ league_code: code });
  };

  // Leave a league
  const leaveLeague = async (leagueId: string) => {
    return leaveLeagueMutation.mutateAsync({ leagueId });
  };

  // Delete a league
  const deleteLeague = async (leagueId: string) => {
    return deleteLeagueMutation.mutateAsync({ leagueId });
  };

  // Note: checkLeagueName should be used as a separate hook

  return {
    leagues,
    getLeague,
    getUserLeagues,
    getCreatedLeagues,
    isLeagueCreator,
    createLeague,
    joinLeague,
    leaveLeague,
    deleteLeague,
    // Query states
    isLoading: userLeaguesQuery.isLoading,
    error: userLeaguesQuery.error,
    refetch: userLeaguesQuery.refetch,
    // Mutation states
    isCreating: createLeagueMutation.isPending,
    isJoining: joinLeagueMutation.isPending,
    isLeaving: leaveLeagueMutation.isPending,
    isDeleting: deleteLeagueMutation.isPending,
  };
};
